export const DRIVER_SAFETY_EVENT_TYPES = [
  "crash",
  "harsh_braking",
  "rapid_acceleration",
  "harsh_cornering",
];

export const DRIVER_SAFETY_EVENT_LABELS = {
  crash: "Crash",
  harsh_braking: "Harsh braking",
  rapid_acceleration: "Rapid acceleration",
  harsh_cornering: "Harsh cornering",
  behaviour: "Behaviour",
};

export const DRIVER_SAFETY_ICON_PRESETS = [
  { value: "dot", label: "Dot" },
  { value: "pin", label: "Pin" },
  { value: "triangle", label: "Triangle" },
  { value: "diamond", label: "Diamond" },
  { value: "shield", label: "Shield" },
];

export const DRIVER_SAFETY_COLOR_PALETTE = [
  { value: "#212121", label: "Charcoal" },
  { value: "#e53935", label: "Red" },
  { value: "#fb8c00", label: "Orange" },
  { value: "#8e24aa", label: "Purple" },
  { value: "#039be5", label: "Blue" },
  { value: "#00897b", label: "Teal" },
  { value: "#6d4c41", label: "Brown" },
  { value: "#546e7a", label: "Slate" },
];

export const DRIVER_SAFETY_HEATMAP_WEIGHTS = {
  crash: 5,
  harsh_braking: 3,
  harsh_cornering: 2,
  rapid_acceleration: 2,
  behaviour: 1,
};

export const DRIVER_SAFETY_HEATMAP_PRESETS = [
  {
    value: "soft",
    label: "Soft",
    description: "Broader and lighter heat spread.",
    radiusPixels: 34,
    intensity: 0.8,
    threshold: 0.02,
    opacity: 0.55,
  },
  {
    value: "balanced",
    label: "Balanced",
    description: "General purpose heatmap profile.",
    radiusPixels: 22,
    intensity: 1,
    threshold: 0.03,
    opacity: 0.75,
  },
  {
    value: "intense",
    label: "Intense",
    description: "Sharper and stronger hotspot contrast.",
    radiusPixels: 16,
    intensity: 1.6,
    threshold: 0.05,
    opacity: 0.9,
  },
];

const HEATMAP_WEIGHTING_SET = new Set(["severity", "uniform"]);
const HEATMAP_PRESET_SET = new Set(
  DRIVER_SAFETY_HEATMAP_PRESETS.map((preset) => preset.value)
);
const DEFAULT_HEATMAP_PRESET =
  DRIVER_SAFETY_HEATMAP_PRESETS.find((preset) => preset.value === "balanced") ||
  DRIVER_SAFETY_HEATMAP_PRESETS[0];
const HEATMAP_LIMITS = {
  radiusPixels: { min: 10, max: 60, defaultValue: 22 },
  intensity: { min: 0.4, max: 3, defaultValue: 1 },
  threshold: { min: 0.01, max: 0.2, defaultValue: 0.03 },
  opacity: { min: 0.15, max: 1, defaultValue: 0.75 },
};

const DEFAULT_DRIVER_SAFETY_STYLES = {
  crash: { iconPreset: "shield", color: "#212121" },
  harsh_braking: { iconPreset: "triangle", color: "#e53935" },
  rapid_acceleration: { iconPreset: "diamond", color: "#fb8c00" },
  harsh_cornering: { iconPreset: "pin", color: "#8e24aa" },
  behaviour: { iconPreset: "dot", color: "#039be5" },
};

export const DEFAULT_DRIVER_SAFETY_MAP_PREF = {
  showMarkers: true,
  useMarkerClustering: true,
  showHeatmap: false,
  showHotspots: true,
  heatmapWeighting: "severity",
  heatmapPreset: DEFAULT_HEATMAP_PRESET.value,
  heatmapRadiusPixels: DEFAULT_HEATMAP_PRESET.radiusPixels,
  heatmapIntensity: DEFAULT_HEATMAP_PRESET.intensity,
  heatmapThreshold: DEFAULT_HEATMAP_PRESET.threshold,
  heatmapOpacity: DEFAULT_HEATMAP_PRESET.opacity,
  styles: DEFAULT_DRIVER_SAFETY_STYLES,
};

const COLOR_SET = new Set(DRIVER_SAFETY_COLOR_PALETTE.map((c) => c.value));
const PRESET_SET = new Set(DRIVER_SAFETY_ICON_PRESETS.map((p) => p.value));

const clampNumber = (value, min, max, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

export const normalizeDriverSafetyMapPrefs = (rawPref) => {
  const pref = rawPref || {};
  const rawStyles = pref.styles || {};
  const styles = {};
  const heatmapPresetValue = HEATMAP_PRESET_SET.has(pref.heatmapPreset)
    ? pref.heatmapPreset
    : DEFAULT_DRIVER_SAFETY_MAP_PREF.heatmapPreset;
  const matchedPreset =
    DRIVER_SAFETY_HEATMAP_PRESETS.find(
      (preset) => preset.value === heatmapPresetValue
    ) || DEFAULT_HEATMAP_PRESET;

  const allKeys = [...DRIVER_SAFETY_EVENT_TYPES, "behaviour"];
  allKeys.forEach((eventType) => {
    const fallback = DEFAULT_DRIVER_SAFETY_STYLES[eventType];
    const maybe = rawStyles[eventType] || {};
    const iconPreset = PRESET_SET.has(maybe.iconPreset)
      ? maybe.iconPreset
      : fallback.iconPreset;
    const color = COLOR_SET.has(maybe.color) ? maybe.color : fallback.color;
    styles[eventType] = { iconPreset, color };
  });

  return {
    showMarkers:
      typeof pref.showMarkers === "boolean"
        ? pref.showMarkers
        : DEFAULT_DRIVER_SAFETY_MAP_PREF.showMarkers,
    useMarkerClustering:
      typeof pref.useMarkerClustering === "boolean"
        ? pref.useMarkerClustering
        : DEFAULT_DRIVER_SAFETY_MAP_PREF.useMarkerClustering,
    showHeatmap:
      typeof pref.showHeatmap === "boolean"
        ? pref.showHeatmap
        : DEFAULT_DRIVER_SAFETY_MAP_PREF.showHeatmap,
    showHotspots:
      typeof pref.showHotspots === "boolean"
        ? pref.showHotspots
        : DEFAULT_DRIVER_SAFETY_MAP_PREF.showHotspots,
    heatmapWeighting: HEATMAP_WEIGHTING_SET.has(pref.heatmapWeighting)
      ? pref.heatmapWeighting
      : DEFAULT_DRIVER_SAFETY_MAP_PREF.heatmapWeighting,
    heatmapPreset: heatmapPresetValue,
    heatmapRadiusPixels: Math.round(
      clampNumber(
        pref.heatmapRadiusPixels,
        HEATMAP_LIMITS.radiusPixels.min,
        HEATMAP_LIMITS.radiusPixels.max,
        matchedPreset.radiusPixels
      )
    ),
    heatmapIntensity: Number(
      clampNumber(
        pref.heatmapIntensity,
        HEATMAP_LIMITS.intensity.min,
        HEATMAP_LIMITS.intensity.max,
        matchedPreset.intensity
      ).toFixed(2)
    ),
    heatmapThreshold: Number(
      clampNumber(
        pref.heatmapThreshold,
        HEATMAP_LIMITS.threshold.min,
        HEATMAP_LIMITS.threshold.max,
        matchedPreset.threshold
      ).toFixed(3)
    ),
    heatmapOpacity: Number(
      clampNumber(
        pref.heatmapOpacity,
        HEATMAP_LIMITS.opacity.min,
        HEATMAP_LIMITS.opacity.max,
        matchedPreset.opacity
      ).toFixed(2)
    ),
    styles,
  };
};

export const getDriverSafetyEventStyle = (driverSafetyMapPref, eventType) => {
  const normalized = normalizeDriverSafetyMapPrefs(driverSafetyMapPref);
  return normalized.styles[eventType] || normalized.styles.behaviour;
};

export const getDriverSafetyIndicatorStyle = ({
  iconPreset,
  color,
  size = 10,
  borderColor = "#fff",
}) => {
  const validPreset = PRESET_SET.has(iconPreset) ? iconPreset : "dot";
  const validColor = COLOR_SET.has(color)
    ? color
    : DEFAULT_DRIVER_SAFETY_STYLES.behaviour.color;
  const border = `1px solid ${borderColor}`;

  if (validPreset === "triangle") {
    return {
      width: 0,
      height: 0,
      borderLeft: `${Math.round(size / 2)}px solid transparent`,
      borderRight: `${Math.round(size / 2)}px solid transparent`,
      borderBottom: `${size}px solid ${validColor}`,
      filter: "drop-shadow(0 0 0.8px rgba(0,0,0,0.35))",
      display: "inline-block",
    };
  }

  if (validPreset === "pin") {
    return {
      width: size,
      height: size,
      background: validColor,
      border,
      borderRadius: "50% 50% 50% 0",
      transform: "rotate(-45deg)",
      display: "inline-block",
      boxSizing: "border-box",
      boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
    };
  }

  if (validPreset === "diamond") {
    return {
      width: size,
      height: size,
      background: validColor,
      border,
      transform: "rotate(45deg)",
      display: "inline-block",
      boxSizing: "border-box",
      boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
    };
  }

  if (validPreset === "shield") {
    return {
      width: size,
      height: Math.round(size * 1.2),
      background: validColor,
      border,
      borderRadius: `${Math.round(size * 0.35)}px ${Math.round(
        size * 0.35
      )}px ${Math.round(size * 0.45)}px ${Math.round(size * 0.45)}px`,
      display: "inline-block",
      boxSizing: "border-box",
      boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
    };
  }

  return {
    width: size,
    height: size,
    borderRadius: "50%",
    background: validColor,
    border,
    display: "inline-block",
    boxSizing: "border-box",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
  };
};

export const buildDriverSafetyIconHtml = ({
  iconPreset,
  color,
  size = 16,
  borderColor = "#fff",
}) => {
  const style = getDriverSafetyIndicatorStyle({
    iconPreset,
    color,
    size,
    borderColor,
  });

  const css = Object.entries(style)
    .map(([k, v]) => {
      const cssKey = k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
      const cssValue = typeof v === "number" ? `${v}px` : v;
      return `${cssKey}:${cssValue}`;
    })
    .join(";");

  return `<div style="${css}"></div>`;
};

export const buildDriverSafetyMapPref = (
  currentUserMapPref,
  nextDriverPref
) => ({
  ...(currentUserMapPref || {}),
  driverSafetyMap: normalizeDriverSafetyMapPrefs(nextDriverPref),
});
